import axios from "axios";

import { updateUser } from "../actions/User";
import store from "../app/store";

const API_URL = process.env.REACT_APP_API_URL;
const SESSION_META_KEY = "auth_session_meta_v1";
const REFRESH_LOCK_KEY = "auth_refresh_lock_v1";
const CHANNEL_NAME = "auth_session_v1";
const LOCK_LEASE_MS = 4000;
const PEER_WAIT_MS = 1500;
const REFRESH_BEFORE_EXPIRY_MS = 30 * 1000;

const tabId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

let initialized = false;
let refreshRequest = null;
let unauthorizedRequest = null;
let channel = null;
let storageHandler = null;
let requestInterceptorId = null;
const tokenUpdateListeners = new Set();

const safeJsonParse = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getUserState = () => store.getState()?.user || {};

const getLocalTokens = () => {
  const user = getUserState();
  return {
    access_token: user?.access_token || "",
    refresh_token: user?.refresh_token || "",
    expires: user?.expires || null,
    expires_at: user?.expires_at || null,
  };
};

const getPersistedTokens = () => {
  const persistedSnapshot = safeJsonParse(localStorage.getItem(SESSION_META_KEY));
  const persistedState = safeJsonParse(localStorage.getItem("retailerPortalState"));
  const user = persistedSnapshot?.tokens ? persistedSnapshot.tokens : persistedState?.user;

  return {
    access_token: user?.access_token || "",
    refresh_token: user?.refresh_token || "",
    expires: user?.expires || null,
    expires_at: user?.expires_at || null,
  };
};

const getSessionMeta = () => {
  const raw = safeJsonParse(localStorage.getItem(SESSION_META_KEY));

  return {
    session_version: Number(raw?.session_version || 0),
    updated_at_ms: Number(raw?.updated_at_ms || 0),
    source_tab_id: raw?.source_tab_id || "",
  };
};

const buildSnapshot = (tokens, options = {}) => {
  const meta = getSessionMeta();
  const nextVersion = Number(options.sessionVersion || meta.session_version + 1);
  const nextUpdatedAt = Number(options.updatedAtMs || Date.now());

  return {
    tokens: {
      access_token: tokens?.access_token || "",
      refresh_token: tokens?.refresh_token || "",
      expires: tokens?.expires || null,
      expires_at: tokens?.expires_at || null,
    },
    session_version: nextVersion,
    updated_at_ms: nextUpdatedAt,
    source_tab_id: options.sourceTabId || tabId,
  };
};

const isIncomingSnapshotNewer = (incoming, current) => {
  if (!incoming) {
    return false;
  }

  if (!current) {
    return true;
  }

  if (incoming.session_version !== current.session_version) {
    return incoming.session_version > current.session_version;
  }

  if (incoming.updated_at_ms !== current.updated_at_ms) {
    return incoming.updated_at_ms > current.updated_at_ms;
  }

  return String(incoming.source_tab_id || "") > String(current.source_tab_id || "");
};

const broadcastSnapshot = (snapshot) => {
  if (!channel) {
    return;
  }

  try {
    channel.postMessage({
      type: "TOKENS_UPDATED",
      payload: snapshot,
    });
  } catch {
    // ignored
  }
};

const notifyTokenUpdate = (snapshot) => {
  tokenUpdateListeners.forEach((listener) => {
    listener(snapshot);
  });
};

const applySnapshot = (snapshot, options = {}) => {
  if (!snapshot?.tokens) {
    return false;
  }

  const currentMeta = getSessionMeta();
  const shouldApply = options.force || isIncomingSnapshotNewer(snapshot, currentMeta);

  if (!shouldApply) {
    return false;
  }

  store.dispatch(
    updateUser({
      access_token: snapshot.tokens.access_token || "",
      refresh_token: snapshot.tokens.refresh_token || "",
      expires: snapshot.tokens.expires || null,
      expires_at: snapshot.tokens.expires_at || null,
    })
  );

  localStorage.setItem(
    SESSION_META_KEY,
    JSON.stringify(snapshot)
  );

  if (options.broadcast !== false) {
    broadcastSnapshot(snapshot);
  }

  notifyTokenUpdate(snapshot);

  return true;
};

const acquireLock = () => {
  const now = Date.now();
  const existing = safeJsonParse(localStorage.getItem(REFRESH_LOCK_KEY));

  if (existing?.lease_expires_at_ms && existing.lease_expires_at_ms > now && existing.owner_tab_id !== tabId) {
    return false;
  }

  const lockPayload = {
    owner_tab_id: tabId,
    lease_expires_at_ms: now + LOCK_LEASE_MS,
    issued_at_ms: now,
  };

  localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lockPayload));
  const confirmed = safeJsonParse(localStorage.getItem(REFRESH_LOCK_KEY));

  return confirmed?.owner_tab_id === tabId;
};

const normalizeExpiresMs = (expires) => {
  const value = Number(expires);

  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  // Directus usually returns milliseconds; if a small seconds value appears, normalize it.
  if (value <= 86400) {
    return value * 1000;
  }

  return value;
};

const isRefreshDue = () => {
  const user = getUserState();
  const expiresAt = Number(user?.expires_at || 0);

  if (!expiresAt) {
    return true;
  }

  return Date.now() >= expiresAt - REFRESH_BEFORE_EXPIRY_MS;
};

const isApiUrl = (url = "") => {
  if (!url || !API_URL) {
    return false;
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url.startsWith(API_URL);
  }

  return true;
};

const getPathFromUrl = (url = "") => {
  if (!url) {
    return "";
  }

  try {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      const parsed = new URL(url);
      return parsed.pathname || "";
    }

    return url;
  } catch {
    return url;
  }
};

const shouldSkipRefreshForRequest = (config = {}) => {
  if (!isApiUrl(config?.url)) {
    return true;
  }

  const path = getPathFromUrl(config?.url);
  if (path.includes("/auth/login") || path.includes("/auth/refresh")) {
    return true;
  }

  return false;
};

const releaseLock = () => {
  const existing = safeJsonParse(localStorage.getItem(REFRESH_LOCK_KEY));
  if (existing?.owner_tab_id === tabId) {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }
};

const waitForPeerUpdate = (baseMeta, timeoutMs = PEER_WAIT_MS) => {
  return new Promise((resolve) => {
    let settled = false;

    const timer = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        tokenUpdateListeners.delete(onUpdate);
        resolve(null);
      }
    }, timeoutMs);

    const onUpdate = (snapshot) => {
      if (settled) {
        return;
      }

      const incomingMeta = {
        session_version: snapshot?.session_version || 0,
        updated_at_ms: snapshot?.updated_at_ms || 0,
        source_tab_id: snapshot?.source_tab_id || "",
      };

      if (isIncomingSnapshotNewer(incomingMeta, baseMeta)) {
        settled = true;
        window.clearTimeout(timer);
        tokenUpdateListeners.delete(onUpdate);
        resolve(snapshot);
      }
    };

    tokenUpdateListeners.add(onUpdate);
  });
};

const refreshFromNetwork = async (refreshTokenAtStart) => {
  const response = await axios.post(`${API_URL}/auth/refresh`, {
    refresh_token: refreshTokenAtStart,
  });

  const authData = response?.data?.data || {};
  const expiresMs = normalizeExpiresMs(authData?.expires);
  const expiresAt = expiresMs ? Date.now() + expiresMs : null;

  const snapshot = buildSnapshot({
    access_token: authData.access_token || "",
    refresh_token: authData.refresh_token || refreshTokenAtStart,
    expires: authData?.expires || null,
    expires_at: expiresAt,
  });
  applySnapshot(snapshot, { broadcast: true });

  return {
    ok: true,
    from: "network",
    tokens: snapshot.tokens,
  };
};

const refreshWithRecovery = async (refreshTokenAtStart) => {
  try {
    return await refreshFromNetwork(refreshTokenAtStart);
  } catch (error) {
    if (error?.response?.status !== 401) {
      return {
        ok: false,
        reason: error?.code === "ECONNABORTED" ? "timeout" : "network",
      };
    }

    const localTokens = getLocalTokens();
    const persistedTokens = getPersistedTokens();
    const latestTokens = {
      access_token: localTokens.access_token || persistedTokens.access_token,
      refresh_token: localTokens.refresh_token || persistedTokens.refresh_token,
    };

    const rotatedElsewhere =
      latestTokens.refresh_token &&
      latestTokens.refresh_token !== refreshTokenAtStart &&
      latestTokens.access_token;

    if (rotatedElsewhere) {
      const snapshot = buildSnapshot(latestTokens);
      applySnapshot(snapshot, { broadcast: false });
      return {
        ok: true,
        from: "peer_update",
        tokens: snapshot.tokens,
      };
    }

    return {
      ok: false,
      reason: "invalid_refresh",
    };
  }
};

const runRefresh = async (options = {}) => {
  const force = options.force === true;

  const refreshTokenAtStart = getUserState()?.refresh_token;
  if (!refreshTokenAtStart) {
    return {
      ok: false,
      reason: "invalid_refresh",
    };
  }

  if (!force && !isRefreshDue()) {
    return {
      ok: true,
      from: "cache",
      tokens: getLocalTokens(),
    };
  }

  const baseMeta = getSessionMeta();
  let ownsLock = acquireLock();

  if (!ownsLock) {
    const snapshot = await waitForPeerUpdate(baseMeta);
    if (snapshot?.tokens?.access_token && snapshot?.tokens?.refresh_token) {
      return {
        ok: true,
        from: "peer_update",
        tokens: snapshot.tokens,
      };
    }

    ownsLock = acquireLock();
  }

  try {
    return await refreshWithRecovery(refreshTokenAtStart);
  } finally {
    if (ownsLock) {
      releaseLock();
    }
  }
};

const refresh = async (options = {}) => {
  if (refreshRequest) {
    return refreshRequest;
  }

  refreshRequest = runRefresh(options).finally(() => {
    refreshRequest = null;
  });

  return refreshRequest;
};

const redirectToLogin = (redirectPath) => {
  if (window.location.pathname === "/login") {
    return;
  }

  const currentPath = redirectPath || `${window.location.pathname}${window.location.search}`;
  const encoded = encodeURIComponent(currentPath);
  window.location.href = `/login?redirect=${encoded}`;
};

const handleUnauthorized = async (redirectPath) => {
  if (window.location.pathname === "/login") {
    return;
  }

  if (unauthorizedRequest) {
    return unauthorizedRequest;
  }

  unauthorizedRequest = (async () => {
    const user = getUserState();
    if (!user?.refresh_token) {
      redirectToLogin(redirectPath);
      return;
    }

    const result = await refresh({ force: true, reason: "request_401" });
    if (!result?.ok) {
      redirectToLogin(redirectPath);
    }
  })().finally(() => {
    unauthorizedRequest = null;
  });

  return unauthorizedRequest;
};

const hydrateSession = async () => {
  const user = getUserState();
  if (!user?.refresh_token) {
    return {
      ok: true,
      from: "cache",
      tokens: getLocalTokens(),
    };
  }

  return refresh({ reason: "hydrate" });
};

const startSchedulers = () => {
  // Intentionally no-op: request-time near-expiry refresh is used instead of interval/focus refresh.
};

const stopSchedulers = () => {
  // Intentionally no-op.
};

const syncFromStore = () => {
  const tokens = getLocalTokens();
  if (!tokens.access_token || !tokens.refresh_token) {
    return;
  }

  const snapshot = buildSnapshot(tokens);
  applySnapshot(snapshot, { broadcast: true });
};

const init = () => {
  if (initialized) {
    return;
  }

  initialized = true;

  if ("BroadcastChannel" in window) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
      const message = event?.data;
      if (message?.type !== "TOKENS_UPDATED") {
        return;
      }

      applySnapshot(message.payload, { broadcast: false });
    };
  }

  storageHandler = (event) => {
    if (event.key === SESSION_META_KEY && event.newValue) {
      const snapshot = safeJsonParse(event.newValue);
      if (
        !snapshot?.tokens ||
        typeof snapshot.session_version !== "number" ||
        typeof snapshot.updated_at_ms !== "number"
      ) {
        return;
      }

      applySnapshot(snapshot, { broadcast: false });
    }
  };

  window.addEventListener("storage", storageHandler);

  requestInterceptorId = axios.interceptors.request.use(async (config) => {
    if (shouldSkipRefreshForRequest(config)) {
      return config;
    }

    await refresh({ reason: "request_time" });

    const latestToken = getUserState()?.access_token;
    if (!latestToken) {
      return config;
    }

    return {
      ...config,
      params: {
        ...(config?.params || {}),
        access_token: latestToken,
      },
    };
  });
};

const dispose = () => {
  stopSchedulers();

  if (storageHandler) {
    window.removeEventListener("storage", storageHandler);
    storageHandler = null;
  }

  if (channel) {
    channel.close();
    channel = null;
  }

  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }

  initialized = false;
};

const authSessionManager = {
  init,
  dispose,
  startSchedulers,
  stopSchedulers,
  syncFromStore,
  hydrateSession,
  refresh,
  handleUnauthorized,
};

export default authSessionManager;
